import { cn } from "@/lib/utils";
import {
  CERTIFICATION_EXPIRY_EMPLOYEE_TYPE,
  CERTIFICATION_EXPIRY_QA_TYPE,
} from "./notification-mapping";
import { resolveNotificationBaseUrl } from "./notification-url";
import { NotificationItemProps } from "./types";

/**
 * Parses simple markdown (bold and newlines) into React elements
 */
function parseMarkdown(text: string): React.ReactNode[] {
  // Handle both literal \n and escaped \\n from backend
  const normalizedText = text.replace(/\\n/g, "\n");
  const lines = normalizedText.split("\n");
  const result: React.ReactNode[] = [];

  lines.forEach((line, lineIndex) => {
    if (lineIndex > 0) {
      result.push(<br key={`br-${lineIndex}`} />);
    }

    // Parse bold text **text**
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    parts.forEach((part, partIndex) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        result.push(
          <strong key={`${lineIndex}-${partIndex}`}>
            {part.slice(2, -2)}
          </strong>,
        );
      } else if (part) {
        result.push(part);
      }
    });
  });

  return result;
}

function normalizeDescription(description: unknown): string {
  if (typeof description === "string") {
    return description;
  }

  if (description == null) {
    return "";
  }

  try {
    return String(description);
  } catch {
    return "";
  }
}

function renderBorrowerDescription(description: unknown) {
  const safeDescription = normalizeDescription(description).replace(
    /\\n/g,
    "\n",
  );

  if (!safeDescription.trim()) {
    return null;
  }

  const dueDateMatch = safeDescription.match(
    /due\s*date(?:\s*:\s*|\s+)[^\n.]+/i,
  );
  if (!dueDateMatch || dueDateMatch.index == null) {
    return <div>{parseMarkdown(safeDescription)}</div>;
  }

  const dueDateStart = dueDateMatch.index;
  const dueDatePhrase = dueDateMatch[0];
  const dueDateEnd = dueDateStart + dueDatePhrase.length;

  const before = safeDescription.slice(0, dueDateStart);
  const after = safeDescription.slice(dueDateEnd);

  return (
    <div>
      {before ? parseMarkdown(before) : null}
      <span className="aero:text-red-600 aero:font-bold">{dueDatePhrase}</span>
      {after ? parseMarkdown(after) : null}
    </div>
  );
}

function renderLibraryBorrowDescription(description: unknown) {
  const normalizedText = normalizeDescription(description).replace(
    /\\n/g,
    "\n",
  );
  const lines = normalizedText
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return null;
  }

  const kvLines = lines.filter((l) => /^\*\*.+?\*\*/.test(l));
  const messageLines = lines.filter((l) => !/^\*\*.+?\*\*/.test(l));

  return (
    <div className="aero:flex aero:flex-col aero:gap-1">
      {kvLines.length > 0 && (
        <div className="aero:flex aero:flex-col aero:gap-1">
          {kvLines.map((line, index) => {
            const match = line.match(/^\*\*(.+?)\*\*\s*(.*)$/);
            if (!match) {
              return <div key={`free-${index}`}>{parseMarkdown(line)}</div>;
            }

            const rawLabel = match[1].trim();
            const isDueDate = rawLabel.toLowerCase().includes("due date");
            const label = rawLabel.replace(/:\s*$/, "");
            const rawValue = (match[2] ?? "").trim();
            const value = isDueDate
              ? rawValue.replace(/[.]+\s*$/, "")
              : rawValue;

            return (
              <div
                key={`kv-${index}`}
                className="aero:grid aero:grid-cols-[140px_8px_1fr] aero:gap-x-1"
              >
                <span className="aero:text-neutral-900">{label}</span>
                <span className="aero:text-neutral-900 aero:text-center">
                  :
                </span>
                <span
                  className={cn(
                    "aero:text-neutral-900 aero:font-bold",
                    isDueDate && "aero:text-red-600",
                  )}
                >
                  {value ? parseMarkdown(value) : null}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {messageLines.length > 0 && (
        <div className="aero:pt-1">
          {messageLines.map((line, index) => (
            <div key={`msg-${index}`}>{parseMarkdown(line)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// The backend has no structured notification fields, so Equipment Collection carries its
// calibration method as a "**Calibration Method** value" line that renders as a chip.
function renderEquipmentCollectionDescription(
  description: unknown,
  isRead: boolean,
) {
  const normalizedText = normalizeDescription(description).replace(
    /\\n/g,
    "\n",
  );
  const lines = normalizedText
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return null;
  }

  return (
    <div className="aero:flex aero:flex-col aero:gap-1">
      {lines.map((line, index) => {
        const methodMatch = line.match(/^\*\*Calibration Method\*\*\s*(.*)$/i);

        if (!methodMatch) {
          return <div key={`msg-${index}`}>{parseMarkdown(line)}</div>;
        }

        return (
          <div
            key={`method-${index}`}
            className="aero:flex aero:items-center aero:gap-2"
          >
            <span>Calibration Method :</span>
            <span
              className={cn(
                "aero:bg-white aero:border aero:border-neutral-500 aero:rounded-full",
                "aero:px-2.5 aero:py-1 aero:inline-flex aero:items-center aero:justify-center aero:shrink-0",
                "aero:text-xs aero:font-semibold aero:tracking-[0.024em]",
                isRead ? "aero:text-neutral-500" : "aero:text-neutral-900",
              )}
            >
              {methodMatch[1].trim()}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function NotificationItem({
  notification,
  onMarkAsRead,
  apps,
}: NotificationItemProps) {
  const isBorrowerReminder =
    notification.notificationType === "BorrowDueReminderNotificationEvent";
  const isLibrarianReminder =
    notification.notificationType ===
    "BorrowDueReminderForLibrarianNotificationEvent";
  const isStockTake =
    notification.notificationType?.startsWith("IssuedToolListStockTake") ||
    notification.notificationType ===
      "MissingToolReportGeneratedNotificationEvent";
  const isEquipmentCollection =
    notification.notificationType ===
    "EquipmentReadyForCollectionNotificationEvent";
  // Certification expiry cards follow the Figma "Notif" layout exactly (WOR-2546): badge next to the title, 4px row gap.
  const isCertificationExpiry =
    notification.notificationType === CERTIFICATION_EXPIRY_EMPLOYEE_TYPE ||
    notification.notificationType === CERTIFICATION_EXPIRY_QA_TYPE;
  const safeDescription = normalizeDescription(notification.description);
  const isDetailedLibraryBorrowReminder = isLibrarianReminder;
  const isLibraryBorrowReminder = isBorrowerReminder || isLibrarianReminder;
  const safeTitle = notification.title ?? "";
  const isOverdue = isLibraryBorrowReminder && /overdue/i.test(safeTitle);
  const showViewDetails = !isBorrowerReminder;
  const displayTitle = safeTitle;
  const badgeLabel = isBorrowerReminder
    ? "Library"
    : isDetailedLibraryBorrowReminder
      ? isOverdue
        ? "Overdue"
        : "Due Monitoring"
      : isStockTake
        ? "Stock Take"
        : notification.label;
  const showBadge =
    Boolean(notification.label) || isLibraryBorrowReminder || isStockTake;

  const cardVariantClass = "aero:border-neutral-300 aero:bg-white";

  const unreadTitleClass = "aero:text-blue-600";

  const unreadTextClass = isBorrowerReminder
    ? "aero:text-neutral-800"
    : isLibrarianReminder
      ? "aero:text-neutral-800"
      : "aero:text-neutral-900";

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (!notification.isRead && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }

    const baseUrl = resolveNotificationBaseUrl(notification, apps);

    const normalizedBase = baseUrl.replace(/\/$/, "");
    const normalizedAction = notification.actionLink.startsWith("/")
      ? notification.actionLink
      : `/${notification.actionLink}`;
    const fullUrl = `${normalizedBase}${normalizedAction}`;

    window.location.href = fullUrl;
  };

  return (
    <div
      className={cn(
        "aero:border aero:rounded aero:p-2",
        cardVariantClass,
        "aero:flex aero:flex-col",
        isCertificationExpiry ? "aero:gap-1" : "aero:gap-2",
      )}
    >
      <div
        className={cn(
          "aero:flex aero:items-center aero:gap-3",
          isCertificationExpiry ? "aero:justify-start" : "aero:justify-between",
        )}
      >
        <h4
          className={cn(
            "aero:text-sm aero:font-bold aero:leading-5",
            isLibraryBorrowReminder
              ? "aero:text-blue-600"
              : notification.isRead
                ? "aero:text-neutral-500"
                : unreadTitleClass,
          )}
        >
          {displayTitle}
        </h4>

        {showBadge && (
          <div
            className={cn(
              "aero:bg-white aero:border aero:border-neutral-500 aero:rounded-full",
              "aero:px-2.5 aero:py-1 aero:inline-flex aero:items-center aero:justify-center aero:shrink-0",
            )}
          >
            <span
              className={cn(
                "aero:text-xs aero:font-semibold",
                isCertificationExpiry
                  ? "aero:leading-[normal] aero:tracking-[0.02em]"
                  : "aero:tracking-[0.024em]",
                isLibraryBorrowReminder
                  ? "aero:text-neutral-900"
                  : notification.isRead
                    ? "aero:text-neutral-500"
                    : "aero:text-neutral-900",
              )}
            >
              {badgeLabel}
            </span>
          </div>
        )}
      </div>

      <div
        className={cn(
          "aero:text-xs aero:leading-4",
          isCertificationExpiry && "aero:pb-[0.6px]",
          isLibraryBorrowReminder
            ? "aero:text-neutral-800"
            : notification.isRead
              ? "aero:text-neutral-500"
              : unreadTextClass,
        )}
      >
        {isBorrowerReminder
          ? renderBorrowerDescription(safeDescription)
          : isDetailedLibraryBorrowReminder
            ? renderLibraryBorrowDescription(safeDescription)
            : isEquipmentCollection
              ? renderEquipmentCollectionDescription(
                  safeDescription,
                  notification.isRead,
                )
              : parseMarkdown(safeDescription)}
      </div>

      {showViewDetails && (
        <a
          href="#"
          onClick={handleClick}
          className={cn(
            "aero:text-xs aero:leading-4 aero:text-blue-500 aero:underline",
            isCertificationExpiry && "aero:pb-[0.6px]",
            "aero:decoration-solid aero:underline-offset-from-font",
            "aero:hover:text-blue-700 aero:transition-colors aero:cursor-pointer",
          )}
        >
          {notification.actionText}
        </a>
      )}
    </div>
  );
}
